/**
 * R32 X4 — the bot-audio LOUDNESS LIMITER (not raw gain).
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * The concierge R58 audio_qa found the TTS output streams QUIET but with ZERO headroom: flash runs at
 * ≈ −24.5 LUFS integrated (perceptually soft on a noisy fair floor) yet its TRUE PEAK is 0.1 dBTP —
 * i.e. it is ALREADY at/over 0 dBFS on inter-sample peaks. So the naive "just turn it up" (a WebAudio
 * `GainNode` > 1.0 on the playback path) would CLIP: any makeup gain applied to a signal already at
 * full-scale peaks hard-clips those peaks into audible distortion. The correct lever is a LIMITER: a
 * `DynamicsCompressor` configured as a brick-wall-ish limiter (low threshold, high ratio, fast attack)
 * that CONTROLS the peaks, followed by a modest MAKEUP GAIN that lifts the now-controlled signal to a
 * louder-but-clean perceived level. Peaks stay ≤ −1 dBTP; perceived loudness rises to ≈ −19..−16 LUFS.
 *
 * ── The insertion point (the R18 AnalyserNode precedent — same node family) ─────────────────────
 * The audible playback path is inside the vendored transport patch's `WavStreamPlayer._start()`:
 *   streamNode.connect(this.context.destination);   // ← the AUDIBLE path
 *   this.analyser.disconnect(); streamNode.connect(this.analyser);   // ← the passive R18 tap
 * The limiter must sit INLINE on the AUDIBLE path: streamNode → compressor → makeupGain → destination
 * (the analyser side-branch is unchanged — lip-sync + level metering still read the pre-limiter
 * signal, which is fine; the tap is about WHAT is spoken, not how loud). That connection is made in
 * the worklet-owning transport code, so wiring the limiter there is a PATCH DELTA — spec'd verbatim
 * for Andy at the bottom of this file / in the review log (the audio-campaign guardrail: pnpm-patch /
 * minified-lib surgery is Andy-direct, NEVER edited by the agent — `docs/LESSONS-voice-audio-debugging.md`).
 *
 * This module is the MAIN-THREAD half (the exact mirror of `jitterBuffer.ts` ↔ the pre-roll patch):
 *   - the LIMITER PARAMS (config, not magic numbers) the patch reads from `globalThis.__coldpressLimiter`;
 *   - `applyLimiter()` — set the params (+ push to a live node if the patch published one);
 *   - the audiodebug post-limiter peak/RMS readout helpers (so `?audiodebug=1` verifies by console).
 * It is INERT until the patch delta is applied: setting globals no un-patched code reads changes
 * nothing (playback is exactly today's — never louder, never distorted, a safe half-applied round).
 *
 * All values are PURE config + pure math (peak/RMS/dBFS) so they are unit-testable without WebAudio.
 */

/**
 * The limiter parameters. A `DynamicsCompressor` acting as a limiter + a makeup `GainNode`.
 *
 * ── Chosen against the R58 finding (flash: −24.5 LUFS integrated, +0.1 dBTP true peak) ──────────
 *   - `thresholdDb: -8`   — start limiting 8 dB below full scale. Because the signal already peaks at
 *                            ~0 dBFS, this catches the loud transients while leaving the (much quieter,
 *                            −24.5 LUFS) body untouched — we're taming peaks, not squashing dialogue.
 *                            8 dB (not 6) leaves room for the +6 dB makeup to land the controlled peaks
 *                            at −2 dBTP (−8 + 6), safely under the ≤ −1 dBTP ceiling with 1 dB margin.
 *   - `ratio: 12`         — a limiting ratio (≥10:1 reads as a limiter, not a gentle compressor): above
 *                            threshold the output barely rises, so peaks are held near the threshold.
 *   - `attackS: 0.003`    — 3ms: fast enough to catch speech transients before they pass, slow enough
 *                            to avoid audible distortion on the fundamental (a true 0ms brick-wall would
 *                            need a look-ahead limiter, which DynamicsCompressor is not — 3ms is the
 *                            pragmatic WebAudio limiter attack).
 *   - `releaseS: 0.25`    — 250ms: recovers smoothly between phrases without pumping.
 *   - `kneeDb: 0`         — a HARD knee (0 dB) so it behaves as a limiter, not a soft compressor.
 *   - `makeupGain: 2.0`   — +6 dB of makeup. With peaks held at the −8 dB threshold by the limiter,
 *                            +6 dB makeup lands the controlled peaks at −2 dBTP (under the ≤ −1 dBTP
 *                            target with 1 dB of margin) while lifting the −24.5 LUFS body by ~6 dB →
 *                            ≈ −18.5 LUFS perceived (inside the −19..−16 target window). Because the
 *                            LIMITER precedes the makeup, this +6 dB CANNOT clip — the limiter has
 *                            already brick-walled the peaks. This is precisely why a raw GainNode (no
 *                            limiter) at the same +6 dB WOULD clip a signal already at 0 dBTP.
 *
 * ⚠️ FIELD-TUNE STATUS: these are the on-paper starting points from the R58 numbers. `makeupGain` is
 * the ONE loudness lever to nudge from the `?audiodebug=1` post-limiter readout: verify the post-limiter
 * true-peak stays ≤ −1 dBTP and the perceived level lands ~−18 LUFS; raise/lower makeup by ≤1 dB steps
 * if the field pass shows headroom to spare / still too soft. Keep the invariant thresholdDb + makeup
 * (dB) ≤ −1. One-place edit (this const), same discipline as the R30 jitter targets.
 */
export type LimiterParams = {
  /** Compressor threshold in dBFS (where limiting begins). */
  thresholdDb: number;
  /** Compression ratio (≥10 ⇒ limiting). */
  ratio: number;
  /** Attack in seconds. */
  attackS: number;
  /** Release in seconds. */
  releaseS: number;
  /** Knee width in dB (0 ⇒ hard knee ⇒ limiter behaviour). */
  kneeDb: number;
  /** Post-limiter makeup gain as a LINEAR multiplier (2.0 ≈ +6 dB). Never applied WITHOUT the limiter
   *  ahead of it — that is the whole point (a raw gain on a 0-headroom signal clips). */
  makeupGain: number;
};

/** The default limiter params — see the LimiterParams doc for the derivation from the R58 finding. */
export const DEFAULT_LIMITER: LimiterParams = {
  thresholdDb: -8,
  ratio: 12,
  attackS: 0.003,
  releaseS: 0.25,
  kneeDb: 0,
  makeupGain: 2.0,
};

/** The makeup-gain safety clamp — never let a mis-set makeup drive a runaway level. Linear (1.0 = 0 dB,
 *  4.0 = +12 dB). The limiter caps peaks regardless, but the clamp bounds the perceived-loudness lever. */
export const MAKEUP_GAIN_MIN = 1.0;
export const MAKEUP_GAIN_MAX = 4.0;

/** Clamp a makeup-gain multiplier into the safe window. */
export function clampMakeupGain(gain: number): number {
  if (!Number.isFinite(gain)) return DEFAULT_LIMITER.makeupGain;
  return Math.max(MAKEUP_GAIN_MIN, Math.min(MAKEUP_GAIN_MAX, gain));
}

/**
 * The global hook the transport patch reads to build + tune the limiter (the exact mirror of
 * `globalThis.__coldpressPreBuffer`). The patch reads the params at `_start()` construction to build
 * the compressor + makeup nodes; `node` is the makeup `GainNode` the patch MAY publish so a live
 * makeup change reaches the currently-playing graph. READ-ONLY from our side for `node` — we only
 * SET the params + optionally set `.gain.value` on a published node.
 */
type LimiterHook = {
  params?: LimiterParams;
  /** Optional: the live makeup GainNode (published by the patch) so a mid-session makeup tweak lands now. */
  node?: { gain: { value: number } };
};

function limiterHook(): LimiterHook {
  const g = globalThis as { __coldpressLimiter?: LimiterHook };
  if (!g.__coldpressLimiter) g.__coldpressLimiter = {};
  return g.__coldpressLimiter;
}

/**
 * Publish the limiter params on the global hook so the next `_start()` builds the limiter from them,
 * and — if the patch has published a live makeup node — push the makeup gain to it immediately (a
 * mid-session tune reaches the playing graph). No-op-safe when no node is published (the params still
 * land on the next worklet). Pure aside from the global hook + the optional node write. INERT until
 * the patch delta is applied.
 */
export function applyLimiter(params: LimiterParams = DEFAULT_LIMITER): void {
  const hook = limiterHook();
  const safe: LimiterParams = { ...params, makeupGain: clampMakeupGain(params.makeupGain) };
  hook.params = safe;
  try {
    if (hook.node) hook.node.gain.value = safe.makeupGain;
  } catch {
    // A stale/torn-down node — the params on the hook still land on the next worklet; never throw
    // into the caller's audio/session path.
  }
}

// ─── audiodebug: post-limiter peak / RMS readout (pure DSP; verified by console under ?audiodebug=1) ──

/** Linear amplitude (0..1) → dBFS. Silence (0) maps to −Infinity; callers format that as "-inf". */
export function amplitudeToDbfs(amplitude: number): number {
  if (amplitude <= 0) return Number.NEGATIVE_INFINITY;
  return 20 * Math.log10(amplitude);
}

/** Peak (max |sample|) of a PCM block, as a linear amplitude 0..1. */
export function blockPeak(samples: Float32Array | number[]): number {
  let peak = 0;
  for (const s of samples) {
    const a = Math.abs(s);
    if (a > peak) peak = a;
  }
  return peak;
}

/** RMS (root-mean-square) of a PCM block, as a linear amplitude 0..1. */
export function blockRms(samples: Float32Array | number[]): number {
  if (samples.length === 0) return 0;
  let sumSq = 0;
  for (const s of samples) sumSq += s * s;
  return Math.sqrt(sumSq / samples.length);
}

/**
 * The post-limiter readout the patch emits (peak/RMS of the LIMITED+makeup'd block, in dBFS) and the
 * `?audiodebug=1` console line format. `peakDb` is the true-peak proxy the field pass reads to confirm
 * ≤ −1 dBTP; `rmsDb` is the loudness proxy (RMS ≈ a rough LUFS stand-in for a single block — the field
 * cares about the trend, not an ITU-R BS.1770 integrated number the client can't cheaply compute).
 */
export type LimiterReadout = { peakDb: number; rmsDb: number };

/** Compute the post-limiter readout for a processed PCM block (pure). */
export function limiterReadout(processed: Float32Array | number[]): LimiterReadout {
  return {
    peakDb: amplitudeToDbfs(blockPeak(processed)),
    rmsDb: amplitudeToDbfs(blockRms(processed)),
  };
}

/** Format a dB value for the console (one decimal; −Infinity → "-inf"). */
export function formatDb(db: number): string {
  if (!Number.isFinite(db)) return '-inf';
  return `${db >= 0 ? '+' : ''}${db.toFixed(1)}`;
}

/**
 * The exact `?audiodebug=1` console line the patch prints (post-limiter). Kept here as the single
 * source of truth so the field pass knows what to look for and the patch delta can be checked against
 * it. Example: `[audiodebug] limiter peak=-1.2dBFS rms=-18.4dBFS makeup=+6.0dB`.
 */
export function limiterDebugLine(readout: LimiterReadout, makeupGain: number): string {
  const makeupDb = 20 * Math.log10(clampMakeupGain(makeupGain));
  return `[audiodebug] limiter peak=${formatDb(readout.peakDb)}dBFS rms=${formatDb(
    readout.rmsDb
  )}dBFS makeup=${formatDb(makeupDb)}dB`;
}
